<script setup lang="ts">
import { computed, ref } from "vue";
import type { DRDrawing, DRPartialProject } from "@/types";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import ProjectAvatar from "@/components/projects/ProjectAvatar.vue";
import { downloadDatabase } from "@/components/files/DrawingList/downloadFile";
import { trackAction, trackTab } from "@/analytics/drawing-analytics";
import BackupsMenu from "@/components/canvas/DrawerMenus/BackupsMenu.vue";
import ActivityMenu from "@/components/canvas/DrawerMenus/ActivityMenu.vue";
import CanvasBrandMark from "./CanvasBrandMark.vue";
import CanvasExitDialog from "./CanvasExitDialog.vue";
import CanvasPanelDialog from "./CanvasPanelDialog.vue";
import { requestMergePanel } from "@/stores/BranchMergeState";
import { openDisplayDialog } from "@/stores/DisplayDialogState";

/**
 * Menu du fichier, tout à gauche de l'en-tête : logo + nom du plan. Regroupe ce
 * qui était éparpillé dans l'ancien en-tête (retour, téléchargement, plein
 * écran) derrière un seul bouton, pour laisser la place aux catalogues, ainsi
 * que les sauvegardes et l'activité du plan (anciens onglets du volet droit,
 * ouverts ici en modale).
 */
const props = defineProps<{
  drawingId: number;
  file?: DRDrawing;
  project?: DRPartialProject;
}>();

const emit = defineEmits<{ (e: "fullscreen"): void }>();

const menuVisible = ref(false);
const exitDialog = ref<InstanceType<typeof CanvasExitDialog>>();
const showBackups = ref(false);
const showActivity = ref(false);

/** Les sauvegardes n'existent que pour un plan enregistré sur le serveur. */
const canShowBackups = computed(
  () => Number.isInteger(props.drawingId) && props.drawingId > 0,
);

/** La fusion n'a de sens que pour un plan enregistré qui a plusieurs versions. */
const canMergeVersions = computed(
  () => canShowBackups.value && (props.file?.branchesCount ?? 0) > 1,
);

const dwgName = computed(() =>
  props.file?.name ? ensureDwgExtension(props.file.name) : "",
);

function ensureDwgExtension(filename: string): string {
  return filename.endsWith(".dwg") ? filename : `${filename}.dwg`;
}

function backToHome() {
  menuVisible.value = false;
  exitDialog.value?.requestExit();
}

async function exportDwg() {
  if (!props.drawingId || !dwgName.value) return;
  menuVisible.value = false;
  trackAction("export_dwg");
  await downloadDatabase(dwgName.value);
}

// Même suivi qu'à l'ouverture des anciens onglets : la page Usage du
// back-office continue de compter leurs visites au même endroit.
function openBackups() {
  menuVisible.value = false;
  showBackups.value = true;
  trackTab("backups", "opened");
}

function openActivity() {
  menuVisible.value = false;
  showActivity.value = true;
  trackTab("activity", "opened");
}

// La fusion s'ouvre dans le volet droit (CanvasDrawersMenus), pour garder le
// plan visible : le conflit courant y est montré.
function openMerge() {
  menuVisible.value = false;
  requestMergePanel();
}

function openDisplay() {
  menuVisible.value = false;
  openDisplayDialog("file_menu");
}

function enterFullscreen() {
  menuVisible.value = false;
  emit("fullscreen");
}
</script>

<template>
  <el-popover
    v-model:visible="menuVisible"
    placement="bottom-start"
    :width="300"
    trigger="click"
    :show-arrow="false"
    popper-class="canvas-popper canvas-file-menu-popper"
  >
    <template #reference>
      <button
        type="button"
        class="canvas-header-chip file-menu-trigger"
        :class="{ open: menuVisible }"
        :aria-label="`${$t('canvas:header.fileMenu.label')} : ${file?.name ?? ''}`"
      >
        <CanvasBrandMark />
        <span class="file-name text-truncate">{{ file?.name ?? "" }}</span>
        <PhosphorIcon name="caret-down" :size="11" weight="fill" />
      </button>
    </template>

    <div class="file-menu" role="menu">
      <!-- Fil d'Ariane (informatif) : projet › plan.dwg -->
      <div class="file-menu-context">
        <template v-if="project?.name">
          <ProjectAvatar class="context-avatar" :project="project" :size="22" />
          <span class="text-truncate context-project">{{ project.name }}</span>
          <PhosphorIcon
            class="context-chevron"
            name="caret-right"
            :size="10"
            weight="bold"
          />
        </template>
        <PhosphorIcon class="context-file-icon" name="file" :size="14" />
        <span class="text-truncate context-file">{{ dwgName }}</span>
      </div>

      <div class="file-menu-sep" />

      <button
        type="button"
        class="file-menu-item"
        role="menuitem"
        @click="backToHome"
      >
        <PhosphorIcon name="house" :size="20" weight="bold" />
        {{ $t("canvas:header.fileMenu.backHome") }}
      </button>

      <div class="file-menu-sep" />

      <button
        type="button"
        class="file-menu-item"
        role="menuitem"
        :disabled="!drawingId || !dwgName"
        @click="exportDwg"
      >
        <PhosphorIcon name="download-simple" :size="20" weight="bold" />
        {{ $t("canvas:header.fileMenu.export") }}
      </button>
      <button
        v-if="canShowBackups"
        type="button"
        class="file-menu-item"
        role="menuitem"
        @click="openBackups"
      >
        <PhosphorIcon name="clock-counter-clockwise" :size="20" weight="bold" />
        {{ $t("canvas:header.fileMenu.backups") }}
      </button>
      <button
        type="button"
        class="file-menu-item"
        role="menuitem"
        @click="openActivity"
      >
        <PhosphorIcon name="clipboard-text" :size="20" weight="bold" />
        {{ $t("canvas:header.fileMenu.activity") }}
      </button>
      <button
        v-if="canMergeVersions"
        type="button"
        class="file-menu-item"
        role="menuitem"
        @click="openMerge"
      >
        <PhosphorIcon name="git-merge" :size="20" weight="bold" />
        {{ $t("canvas:header.fileMenu.mergeVersions") }}
      </button>

      <div class="file-menu-sep" />

      <button
        type="button"
        class="file-menu-item"
        role="menuitem"
        @click="openDisplay"
      >
        <PhosphorIcon name="sliders-horizontal" :size="20" weight="bold" />
        {{ $t("canvas:header.fileMenu.display") }}
      </button>
      <button
        type="button"
        class="file-menu-item"
        role="menuitem"
        @click="enterFullscreen"
      >
        <PhosphorIcon name="corners-out" :size="20" weight="bold" />
        {{ $t("canvas:header.fileMenu.fullscreen") }}
      </button>
    </div>
  </el-popover>

  <CanvasExitDialog ref="exitDialog" />

  <CanvasPanelDialog
    v-model="showBackups"
    :title="$t('canvas:settings.backups.section.title')"
  >
    <template #default="{ close }">
      <BackupsMenu :drawing-id="drawingId" @close="close" />
    </template>
  </CanvasPanelDialog>

  <CanvasPanelDialog
    v-model="showActivity"
    :title="$t('canvas:activity.title')"
  >
    <template #default="{ close }">
      <ActivityMenu :drawing-id="drawingId" @close="close" />
    </template>
  </CanvasPanelDialog>
</template>

<style lang="scss" scoped>
.file-menu-trigger {
  gap: 7px;
  font-weight: 500;
  // Le nom se tronque, le logo et la flèche jamais : contrairement aux autres
  // boutons de l'en-tête, celui-ci rétrécit.
  flex-shrink: 1;
  min-width: 0;
  max-width: 260px;
}

.file-name {
  min-width: 0;
}

.file-menu {
  display: flex;
  flex-direction: column;
}

// Une seule ligne, comme la maquette V5 : le nom du projet se tronque avant
// celui du plan.
.file-menu-context {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  padding: 8px 10px 10px;
  font-size: 12.5px;
  white-space: nowrap;
}

.context-avatar {
  flex-shrink: 0;
  padding: 0 !important; // ProjectAvatar pose un p-2, trop pour 22 px
  border-radius: 6px !important;
}

.context-project {
  flex: 0 3 auto;
  min-width: 0;
  color: rgba(255, 255, 255, 0.65);
}

.context-chevron {
  color: rgba(255, 255, 255, 0.35);
}

.context-file-icon {
  color: rgba(255, 255, 255, 0.7);
}

.context-file {
  flex: 0 1 auto;
  min-width: 0;
  color: #fff;
  font-weight: 500;
}

.file-menu-sep {
  height: 1px;
  margin: 4px 0;
  background-color: var(--canvas-border);
}

.file-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 40px; // cible tactile
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #fff;
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  :deep(svg) {
    color: rgba(255, 255, 255, 0.7);
  }

  &:hover:not(:disabled) {
    background-color: var(--canvas-btn-hover-bg);
  }

  &:disabled {
    color: var(--canvas-btn-disabled-color);
    cursor: not-allowed;
  }
}
</style>
